import {Injectable} from '@angular/core';
import {from, Observable, of, Subject} from "rxjs";
import {DataResult, DataStateChangeEventArgs, SortDescriptorModel, Sorts} from "@syncfusion/ej2-angular-grids";
import {TableGridListContext} from "./table-grid-list-context";
import {map} from "rxjs/operators";
import {RequestService} from "../../../services/request/request.service";
import {ListDataContext} from "./list-data-context";
import {ControllerQueryOrderByModel} from "../../../services/request/controller-query-order-by.model";
import {TableGridType} from "./table-grid-type.enum";
import {cloneDeep} from 'lodash';
import {InternationalizationService} from "../../../services/internationalization/internationalization.service";
import {LocationService} from "../../../services/location/location.service";
import {Decimal} from "decimal.js-light";

@Injectable({
  providedIn: 'root'
})
export class ListDataService extends Subject<DataStateChangeEventArgs> {

  public escapeRegex: RegExp = /[&<>"']/g;

  public escapeEntities: { [x: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&#34;',
    '\'': '&#39;'
  };

  private QUERY_EMPTY = '{"and":[]}';

  constructor(private requestService: RequestService,
              private i18nService: InternationalizationService,
              private locationService: LocationService) {
    super();
  }

  public execute(state: DataStateChangeEventArgs, context: ListDataContext): void {
    this.getData(state, context).subscribe(x => super.next(x));
  }

  public executeExplicitData(state: DataStateChangeEventArgs, context: ListDataContext, data: any[], recordCount: number): void {
    this.getExplicitData(state, context, data, recordCount).subscribe(x => super.next(x));
  }

  public async executeReturningPromise(state: DataStateChangeEventArgs, context: ListDataContext) {
    return this.getData(state, context).toPromise();
  }

  public executeExplicitDataReturningPromise(state: DataStateChangeEventArgs, context: ListDataContext, data: any[], recordCount: number) {
    return this.getExplicitData(state, context, data, recordCount).toPromise();
  }

  // In some places the sort order coming from syncfusion is reversed. This might be a bug that is fixed in a later version,
  // so we pull out the compensating code into a function.
  public patchDataGridSortOrder(state: DataStateChangeEventArgs): DataStateChangeEventArgs {
    const patchedState = cloneDeep(state);
    if (patchedState.sorted) {
      patchedState.sorted = patchedState.sorted.reverse();
    }
    return patchedState;
  }

  // The definitions of Sorts and SortDescriptorModel are inconsistent.
  public patchDataGridSortOrderToSortSettingsSortOrder(sorts: Sorts[]): SortDescriptorModel[] {
    const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    return sorts.map(sort => {
      return {field: sort.name, direction: capitalize(sort.direction)};
    })
  }

  public convertToControllerQuerySorted(sorted?: Sorts[]): ControllerQueryOrderByModel[] {
    return (!sorted || sorted.length == 0) ? [] : sorted.map((s) => {
      return {
        orderBy: s.name,
        direction: (s.direction === "descending") ? "desc" : "asc"
      };
    });
  }

  public convertToDataGridSorted(sortOrder: string): any[] {
    if (!sortOrder || sortOrder === "") {
      return [];
    }

    // Format is something like "Order:desc,Number:asc"
    // We convert it into SF format [{ name: "Order", direction: "descending" }]
    return sortOrder.replace(/ /g, "").split(",").map((so) => {
      const parts = so.split(":");
      const field = parts[0];
      const direction = parts.length > 1 ? {asc: 'ascending', desc: 'descending'}[parts[1]] : 'ascending';
      return {name: field, direction: direction};
    });
  }

  public convertToStorageSorted(sorted?: Sorts[]): string {
    // Format is something like "Order:desc,Number:asc"
    if (!sorted || sorted.length === 0) {
      return '';
    }

    return sorted.map((s) => {
      return `${s.name}:${s.direction === "descending" ? "desc" : "asc"}`
    }).join(',');
  }

  public sortedByOrderColumn(listModel: any): string {
    return "";
  }

  public getData(state: DataStateChangeEventArgs, context: ListDataContext): Observable<DataStateChangeEventArgs> {
    let result: Observable<DataStateChangeEventArgs>;
    const table = context.table;
    const tileID = context.tileID;
    const tile = context.tile;
    const method = context.method;
    const data = context.data;
    const aspect = context.aspect;
    const firstResult = state.skip || 0;
    const maxResults = state.take || 10;
    let sortOrder: ControllerQueryOrderByModel[] = state.sorted ?
      this.convertToControllerQuerySorted(state.sorted) :
      this.convertToControllerQuerySorted(this.convertToDataGridSorted(context.customSortOrder));

    let jsonQuery = context.query && context.query !== this.QUERY_EMPTY ? JSON.parse(context.query) : undefined; // needs to be undefined not null

    // Column query comes from the column filter boxes above the columns
    const columnQuery = context.columnQuery && context.columnQuery !== this.QUERY_EMPTY ? JSON.parse(context.columnQuery) : undefined;
    if (columnQuery && jsonQuery) {
      jsonQuery = {and: [jsonQuery, columnQuery]};
    } else if (columnQuery) {
      jsonQuery = columnQuery;
    }

    const query = this.requestService.createControllerQuery();

    query.firstResult(firstResult);
    query.maxResults(maxResults);
    query.sortOrder(sortOrder);
    query.query(jsonQuery);

    // This is a related list that has had its value changed by the quick entry but hasn't been saved yet, so we need
    // to find the entries by id.
    if (context.relatedListIds !== null && context.relatedListIds !== undefined) {
      query.query({
        and: [
          {
            fieldName: "id",
            operator: "in",
            value: context.relatedListIds
          }
        ]
      });
    }

    if (context.type === TableGridType.List) {
      // In certain circumstances the current query is not applicable to the top of the history list. This can be
      // triggered by the file upload renderer when it is also displaying the list of attachments. We don't want to
      // save the current query when this happens.
      // TODO: it doesn't seem that this happens anymore, so we don't need this check.
      let save = true;
      const content = this.locationService.getCurrentPageURL();
      if (content) {
        // Use a fake base and use URL to pull the string apart
        const url = new URL(content, "https://xxx/")
        const parts = url.pathname.split('/');
        if (parts && parts.length > 1) {
          const historyTable = parts[1];
          if (historyTable !== table) {
            save = false;
          }
        }
      }

      if (save) {
        this.locationService.updateCurrentQuery({
          query: jsonQuery,
          firstResult: firstResult,
          maxResults: maxResults
        });
      }
    }

    // Add applyAllowReadFromParent if supplied.
    let requestData = data;
    if (context.readFromParentData) {
      requestData = {...data, ...context.readFromParentData};
    }

    // Add aggregates if supplied in the context
    if (state.aggregates) {
      requestData["aggregates"] = this.convertAggregatesTo(cloneDeep(context.aggregates));
    }

    const obs: Observable<TableGridListContext> =
      from(this.requestService.createRequest()
        .table(table)
        .tile(tile)
        .tileID(tileID)
        .method(method)
        .aspect(aspect)
        .data(requestData)
        .controllerQuery(query)
        .execute() as Promise<TableGridListContext>);

    result = obs.pipe(map((response: TableGridListContext) => {
        // Copy the raw data back to our list model otherwise it gets out of sync
        context.listModel.data = response.data;
        context.listModel.aggregates = this.convertAggregatesFrom(cloneDeep(response.aggregates));
        // Transform the data into a form that the grid wants
        const records = response.data.map((d) => {
          return this.buildMappedData(d, context);
        });

        return (<DataResult>{
          result: records,
          count: response.recordCount,
        })
      }
    ))
      .pipe((data: any) => data);

    return result;
  }

  public getExplicitData(state: DataStateChangeEventArgs, context: ListDataContext, data: any[], recordCount: number): Observable<DataStateChangeEventArgs> {
    const mappedData = data.map((d) => {
      return this.buildMappedData(d, context);
    });

    const dataResult: DataResult = {
      result: mappedData,
      count: recordCount
    };

    return of(dataResult).pipe((data: any) => data);
  }

  private convertAggregatesTo(aggregates: any[]) : any {
    if (aggregates) {
      aggregates.forEach((ag) => {
        if (ag.type === "Average") {
          ag.type = "Avg";
        }
      });
    }
    return aggregates;
  }

  private convertAggregatesFrom(aggregates: any[]) : any {
    if (aggregates) {
      aggregates.forEach((ag) => {
        if (ag.type === "Avg") {
          ag.type = "Average";
        }
      });
    }
    return aggregates;
  }

  private buildMappedData(d: any, context: ListDataContext): any {
    const _data = {
        record: d,
        id: d['id'],
        tableClass: d['_tableClass'],
        listModel: context.listModel,
        relationshipData: context.data, // For related lists
        linkForUI: d['_linkForUI'],
        sequenceNumber: d['_sequenceNumber'],
        identifier: d['_identifier'],
    };
    const result: any = {
      primaryKey: d['id'], // Used to support persistSelection
      Id: d['id']
    };

    // Properties that match /^_[a-z0-9]/ are not real fields. Properties that match /^_[A-Z]/ might be real fields, e.g. data import staging tables.
    Object.getOwnPropertyNames(d).filter((p) => {
      return !(p.match(/^_[a-z0-9]/) || p === 'id')
    }).map((p, idx) => {
      if (d[p].record) {

        result[p] = this.sanitize(d[p].record?.value?.displayValue);

        _data[p] = {
          fieldType: 'REFERENCE',
          id: d[p].record.id,
          style: d[p].style,
          tableName: d[p].record._tableClass,
          value: d[p].record?.value?.value,
          displayValue: this.sanitize(d[p].record?.value?.displayValue),
        };

      } else if (d[p].records) {

        result[p] = d[p].records.map((record) => this.sanitize(record?.value?.displayValue)).join(',');

        _data[p] = {
          fieldType: 'MULTIPLE_REFERENCE',
          style: d[p].style,
          records: d[p].records.map((record) => {
            return {
              id: record.id,
              tableName: record._tableClass,
              value: record?.value?.value,
              displayValue: this.sanitize(record?.value?.displayValue)
            }
          })
        };

      } else {
        // Certain field types require the native type rather than string. Note that the field type in the table
        // definition has different names from those in the view definition.
        let fieldDef = context.listModel.tableDefinition._fields.find(f => f._fieldName === p);

        if (fieldDef == null) {
          // There are certain times when a field is not available (Permissions/Sub-Table table)
          // So return an empty model for the list view
          _data[p] = {
            fieldType: 'TEXT',
            style: null,
            value: null,
            displayValue: null,
          };
        } else {
          const fieldType = fieldDef._fieldType;

          if (['BOOLEAN', 'DURATION', 'INTEGER', 'LONG', 'DECIMAL', 'ORDER'].includes(fieldType)) {
            result[p] = d[p].value;
          } else if (fieldType === 'MONEY') {
            let value = d[p].value && !isNaN(d[p].value) ? d[p].value : 0;
            result[p] = new Decimal(value);
          } else if (fieldType === 'DATE_TIME') {
            result[p] = d[p].value ? new Date(d[p].value) : null;
          } else if (fieldType === 'DATE') {
            // SER-2922 Parse date to return JS Date at midnight in current browser timezone
            result[p] = d[p].value ? this.i18nService.parseDate(d[p].value, {format: 'yyyy-MM-dd'}) : null;
          } else {
            result[p] = this.sanitize(d[p].displayValue);
          }

          _data[p] = {
            fieldType: fieldType,
            style: d[p].style,
            value: d[p].value,
            displayValue: this.sanitize(d[p].displayValue),
          };
        }
      }
    });

    result._dataFn = () => {
      return _data;
    };

    return result;
  }

  sanitize(value: any): any {
    // Use this to make content that looks like HTML display as escaped
    return value?.replace(this.escapeRegex, (c: string) => {
      return this.escapeEntities[c];
    });
  }
}
